import type React from 'react';
import { toHTML } from 'slack-markdown';
import timezones from 'timezones-list';
import TurndownService from 'turndown';

import type { SOURCE_TYPE } from '@/types/feedbackConst';
import { parse } from '@/utils/marked';

export const DEFAULT_HIGHLIGHT_TARGET = 'highlight-target';

export function arrayToMap<T>(array: T[], key: keyof T): Record<keyof T, T> {
  return array.reduce(
    (acc, value) => {
      const targetValue = value[key];
      if (typeof targetValue !== 'string' && typeof targetValue !== 'number') {
        throw new Error('Key must be string or number');
      }
      return { ...acc, [targetValue]: value };
    },
    {} as Record<keyof T, T>,
  );
}

export const pushEntryIntoQueryArr = (
  prevArr: QueryT[],
  map: Record<string, string | null>,
): QueryT[] => {
  const newArr = [...prevArr];
  Object.keys(map).forEach((key) => {
    const value = map[key];
    if (value) {
      newArr.push({
        key,
        value,
      });
    }
  });
  return newArr;
};

export function buildQuery(
  queries: QueryT[],
  queryKeyMap?: Record<string, string>,
): string {
  const params = new URLSearchParams();
  queries.forEach((query) => {
    if (!query.value) return;
    if (queryKeyMap) {
      params.append(queryKeyMap[query.key] || query.key, query.value);
    } else {
      params.append(query.key, query.value);
    }
  });
  return `?${params.toString()}`;
}

export function queryArrToObj(queryArr: QueryT[]): QueryObj {
  const newObj: QueryObj = {};
  const len = queryArr.length;
  for (let i = 0; i < len; i++) {
    if (queryArr[i].value === null) continue;
    newObj[queryArr[i].key] = queryArr[i].value;
  }

  return newObj;
}

export function buildObjQuery(
  queries: QueryObj,
  queryKeyMap?: Record<string, string>,
): string {
  const params = new URLSearchParams();
  Object.keys(queries).forEach((key) => {
    const value = queries[key];
    if (value === null) return;
    if (value === undefined) return;
    if (queryKeyMap) {
      params.append(queryKeyMap[key] || key, value.toString());
    } else {
      params.append(key, value.toString());
    }
  });
  if (params.toString().trim() === '') return '';
  return `?${params.toString()}`;
}

type RouterQueryT = {
  [key: string]: string;
};

export function getQueries(routerQuery: RouterQueryT): QueryT[] {
  return Object.keys(routerQuery).map((key) => ({
    key,
    value: routerQuery[key],
  }));
}

export function addQuery(routerQuery: RouterQueryT, query: QueryT): string {
  const dupQuery = { ...routerQuery };
  if (!query.value) return buildQuery(getQueries(dupQuery));
  dupQuery[query.key] = query.value;
  const currQuery = getQueries(dupQuery);
  return buildQuery(currQuery);
}

export function removeQuery(
  routerQuery: RouterQueryT,
  queryKey: string,
): string {
  const currQuery = getQueries(routerQuery);
  const targetIndex = currQuery.findIndex((query) => query.key === queryKey);
  if (targetIndex === -1) return buildQuery(currQuery);
  const newQuery = [
    ...currQuery.slice(0, targetIndex),
    ...currQuery.slice(targetIndex + 1),
  ];
  return buildQuery(newQuery);
}

export const getTimezoneList = () => {
  const timezoneInfos = [] as { name: string; tzCode: string }[];
  for (const timezone of timezones) {
    timezoneInfos.push({
      name: timezone.label,
      tzCode: timezone.tzCode,
    });
  }
  return timezoneInfos;
};

export const parseFullContent = (jsonString: string) => {
  return JSON.parse(jsonString).reduce((acc: Array<any>, value: any) => {
    acc.push(value);
    return acc;
  }, []);
};

export const charToNumber = (char: string): number => {
  const result = Number('0xF1' + char.charCodeAt(0).toString(10));
  return result % 10;
};

export const createQueryString = (
  object: { [key: string]: any },
  Map: { [key: string]: any },
) => {
  let query = '';
  Object.keys(object).map((key, index) => {
    const item = Map[key];

    if (!item) return;

    if (index !== Object.keys(object).length - 1) query += item.name + ',';
    else query += item.name;
  });

  return query;
};

export function isValidURL(string: string) {
  var res = string.match(
    /(http(s)?:\/\/.)?(www\.)?[-a-zA-Z0-9@:%._+~#=]{2,256}\.[a-z]{2,6}\b([-a-zA-Z0-9@:%_+.~#?&//=]*)/g,
  );
  return res !== null;
}

export const stopEvent = (e: React.KeyboardEvent) => {
  e.stopPropagation();
};

export const stringSort = (a: string, b: string) => {
  // isLocaleCompare의 기본 동작은 숫자 -> 한글 -> 영어 순으로 정렬되는데, 이를 숫자 영어 한글 순으로 정렬하기 위해 Intl.Collator 활용
  const collator = new Intl.Collator('en', { numeric: true });
  return collator.compare(a, b);
};

export function inverseMap(obj: any) {
  const retobj = {} as any;
  for (const key in obj) {
    retobj[obj[key]] = key;
  }
  return retobj;
}

export function getOS() {
  var userAgent = window.navigator.userAgent,
    platform = window.navigator.platform,
    macosPlatforms = ['Macintosh', 'MacIntel', 'MacPPC', 'Mac68K'],
    windowsPlatforms = ['Win32', 'Win64', 'Windows', 'WinCE'],
    iosPlatforms = ['iPhone', 'iPad', 'iPod'],
    os = null;

  if (macosPlatforms.indexOf(platform) !== -1) {
    os = 'Mac OS';
  } else if (iosPlatforms.indexOf(platform) !== -1) {
    os = 'iOS';
  } else if (windowsPlatforms.indexOf(platform) !== -1) {
    os = 'Windows';
  } else if (/Android/.test(userAgent)) {
    os = 'Android';
  } else if (!os && /Linux/.test(platform)) {
    os = 'Linux';
  }

  return os;
}

const WINDOWS_1252 =
  '\u0000\u0001\u0002\u0003\u0004\u0005\u0006\u0007\b\t\n\u000b\f\r\u000e\u000f\u0010\u0011\u0012\u0013\u0014\u0015\u0016\u0017\u0018\u0019\u001a\u001b\u001c\u001d\u001e\u001f !"#$%&\'()*+,-./0123456789:;<=>?@ABCDEFGHIJKLMNOPQRSTUVWXYZ[\\]^_`abcdefghijklmnopqrstuvwxyz{|}~€�‚ƒ„…†‡ˆ‰Š‹Œ�Ž��‘’“”•–—˜™š›œ�žŸ ¡¢£¤¥¦§¨©ª«¬­®¯°±²³´µ¶·¸¹º»¼½¾¿ÀÁÂÃÄÅÆÇÈÉÊËÌÍÎÏÐÑÒÓÔÕÖ×ØÙÚÛÜÝÞßàáâãäåæçèéêëìíîïðñòóôõö÷øùúûüýþÿ';

export function fromWindows1252(binaryString: string) {
  let text = '';

  for (var i = 0; i < binaryString.length; i++) {
    text += WINDOWS_1252.charAt(binaryString.charCodeAt(i));
  }

  return text;
}

export function isMac() {
  return getOS() === 'Mac OS';
}

export function getPlainTextFromRichText(body: string, source: SOURCE_TYPE) {
  const parser = new DOMParser();
  const textHTML = source === 3 ? toHTML(body) : parse(body);
  const doc = parser.parseFromString(textHTML, 'text/html');
  return doc.documentElement.innerText;
}

export function getPlainTextFromHTML(html: string) {
  const parser = new DOMParser();
  const doc = parser.parseFromString(html, 'text/html');
  return doc.documentElement.innerText;
}

export function getHTMLFromString(val: string) {
  const parser = new DOMParser();
  const doc = parser.parseFromString(val, 'text/html');
  return doc;
}

export function capitalizeFirstLetterInWord(word: string) {
  return word.charAt(0).toUpperCase() + word.slice(1);
}

export function convertToUTF8(windows1252Str: string) {
  const decoder = new TextDecoder('windows-1252');
  const decodedStr = decoder.decode(
    new Uint8Array(windows1252Str.split('').map((c) => c.charCodeAt(0))),
  );

  const encoder = new TextEncoder();
  const utf8Arr = encoder.encode(decodedStr);
  const numberArray = Array.from(utf8Arr);

  return String.fromCharCode.apply(null, numberArray);
}

export function isBase64Image(str: string) {
  const base64Regex = /^data:image\/(png|jpg|jpeg|gif);base64,/;
  return base64Regex.test(str);
}

export function isNumber(str: string): boolean {
  if (typeof str !== 'string') return false;
  if (str.trim() === '') return false;

  return !Number.isNaN(Number(str));
}

export function formatNumber(number: number) {
  return number.toLocaleString();
}

export function generateRandomString() {
  return (Math.random() + 1).toString(36).substring(7);
}

export function addProtocol(link: string) {
  if (/^(http|https):\/\//i.test(link)) {
    return link;
  } else {
    return `http://${link}`;
  }
}

export function htmlToMarkdown(htmlText: string) {
  const turndownService = new TurndownService();

  turndownService.addRule('bullet-list', {
    filter: 'li',
    replacement: (innerHTML, node) => {
      let list_items = '';

      if (node.nodeType === node.ELEMENT_NODE) {
        const element = node as Element;
        if (element.parentElement?.tagName === 'OL') {
          let index = 0;
          let previousClassName =
            element.parentElement.children[0]?.className || '';
          for (const child of element.parentElement.children) {
            if (
              element.className !== '' &&
              previousClassName !== child.className
            ) {
              index = 0;
            }
            if (child.tagName === 'LI' && child.isSameNode(element)) {
              break;
            } else if (
              child.tagName === 'LI' &&
              child.className === element.className
            ) {
              index += 1;
            }
            previousClassName = child.className;
          }
          const className = element.className.replace('ql-indent-', '');
          const depth = parseInt(className, 10) || 0;
          const prefix = String(index + 1);
          for (let i = 0; i < depth; i++) {
            list_items += '    ';
          }
          list_items += prefix + '.  ' + innerHTML + '\n';
        } else if (element.parentElement?.tagName === 'UL') {
          const className = element.className.replace('ql-indent-', '');
          const depth = parseInt(className, 10) || 0;
          for (let i = 0; i < depth; i++) {
            list_items += '    ';
          }
          list_items += '*' + '  ' + innerHTML + '\n';
        }
      }
      return list_items;
    },
  });

  turndownService.addRule('mark', {
    filter: 'mark',
    replacement: (content) => {
      return `==${content}==`;
    },
  });
  turndownService.addRule('underline', {
    filter: 'u',
    replacement: (content) => {
      return `<u>${content}</u>`;
    },
  });

  // Add custom rule for <s> and <strike> tags
  turndownService.addRule('strike', {
    filter: 's',
    replacement: (content) => {
      return `~~${content}~~`;
    },
  });

  turndownService.addRule('code-block', {
    filter: 'pre',
    replacement: (content) => {
      return `\`\`\`\n${content}\n\`\`\``;
    },
  });

  turndownService.addRule('empty-line', {
    filter: 'br',
    replacement: (content) => {
      return '%%EMPTYLINE%%';
    },
  });

  const markdownText = turndownService.turndown(htmlText);
  return markdownText;
}

export const nullCheckToString = (value: any) => {
  return value ? value.toString() : '';
};

export function changeDelTagToSTag(htmlText: string) {
  let result = htmlText;

  const regex = /<del>(.*?)<\/del>/g; // g 플래그로 전역 검색 활성화
  const matches = htmlText.match(regex); // 정규표현식에 매칭되는 모든 부분을 추출
  if (matches) {
    for (const match of matches) {
      const regex = /<del>(.*?)<\/del>/; // 정규표현식 패턴
      const matches = match.match(regex); // 정규표현식에 매칭되는 부분을 추출
      if (matches) {
        const text = matches[1];
        result = result.replace(match, `<s>${text}</s>`);
      }
    }
  }
  const parser = new DOMParser();
  const doc = parser.parseFromString(result, 'text/html');
  const liElems = doc.getElementsByTagName('li');
  if (liElems.length > 0) {
    for (const element of liElems) {
      if (element.className === 'clone') {
        continue;
      }
      let elem = element as HTMLElement;
      let parent = elem.parentElement;
      let index = -1;
      for (;;) {
        if (
          elem.parentElement?.tagName === 'OL' ||
          elem.parentElement?.tagName === 'UL'
        ) {
          index += 1;
          if (parent?.tagName === elem.parentElement.tagName) {
            parent = elem.parentElement;
          }
        } else if (elem.parentElement?.tagName === 'BODY') {
          break;
        }
        if (!elem.parentElement) break;
        elem = elem.parentElement;
      }
      if (index > 0) {
        if (element.children.length === 0 && !element.nextElementSibling) {
          const clone = element.cloneNode(true) as HTMLElement;
          clone.className = 'clone';
          clone.textContent = '$$CLONE$$';
          element.parentElement?.appendChild(clone);
        }
        element.className = `ql-indent-${index}`;
      }
    }
  }

  return doc.documentElement.innerHTML;
}

export const createOptionsFromValues = (
  columnKey: string,
  values: string[],
) => {
  return values.map((value) => ({
    columnKey,
    filterKey: value,
    filterName: value,
    filterOptionNode: value,
  }));
};

export const getPercent = (total: number, compare: number): number => {
  if (isNaN(total) || isNaN(compare)) return 0;
  if (total === 0 || compare === 0) return 0;
  return (compare / total) * 100;
};

export const getPercentStringFormat = (
  total: number,
  compare: number,
  fixed = 1,
) => {
  return getPercent(total, compare).toFixed(fixed);
};

export const highlight = (text: string, selector?: string) => {
  const targets = document.querySelectorAll(
    selector || `.${DEFAULT_HIGHLIGHT_TARGET}`,
  );
  removeHighlight(selector);
  targets.forEach((target) => {
    const index = target.innerHTML
      .toLocaleLowerCase()
      .indexOf(text.toLowerCase());
    if (index >= 0) {
      const innerHTML =
        target.innerHTML.substring(0, index) +
        "<span class='highlight'>" +
        target.innerHTML.substring(index, index + text.length) +
        '</span>' +
        target.innerHTML.substring(index + text.length);
      target.innerHTML = innerHTML;
    }
  });
};

export const removeHighlight = (selector?: string) => {
  const targets = document.querySelectorAll(
    selector || `.${DEFAULT_HIGHLIGHT_TARGET}`,
  );
  targets.forEach((target) => {
    const innerHTML = target.innerHTML.replaceAll(/(<([^>]+)>)/gi, '');
    target.innerHTML = innerHTML;
  });
};

export const clipboardCopy = (data: string) => {
  navigator.clipboard.writeText(data);
};

export const calIncrementAmount = (
  current_count: number,
  past_count: number,
) => {
  if (!past_count && !current_count) return 0;
  // new 인 경우
  if (past_count === 0) return null;
  return Number((((current_count - past_count) / past_count) * 100).toFixed(1));
};

export const timeOut = (delay: number) => {
  return new Promise((resolve) => {
    setTimeout(() => {
      resolve(delay);
    }, delay);
  });
};

export const lowerCaseMatch = (
  origin?: string | null,
  compare?: string | null,
): boolean => {
  if (!origin) return false;
  return !!origin
    .toLocaleLowerCase()
    .includes(compare?.toLocaleLowerCase() || '');
};

export const undefinedException = <T>(value?: T) => {
  if (typeof value === 'undefined') return false;
  return true;
};

export const isEmptyString = (string?: string | number | null) => {
  if (typeof string === 'undefined') return true;
  if (string === null) return true;
  if (typeof string === 'number') return false;
  return string.trim() === '';
};

export const isEmptyArray = (array?: any[]) => {
  if (!array) return true;
  return array.length === 0;
};

export const isInteger = (number?: number) => {
  return typeof number !== 'undefined';
};

export const arrayItemToggle = <T extends {}>(
  arr: T[],
  value: T,
  callback: () => boolean,
) => {
  const newArr = [...arr];
  const targetIndex = newArr.findIndex(callback);
  if (targetIndex === -1) {
    newArr.push(value);
  } else {
    newArr.splice(targetIndex, 1);
  }
  return newArr;
};

export const omitEmpty = <T extends object>(obj: T) => {
  return Object.fromEntries(
    Object.entries(obj).filter(
      ([_, value]) => value !== null && value !== undefined,
    ),
  );
};

export const validateEmptyArray = <T extends any[]>(
  array?: T,
): T | undefined => {
  if (!array) return undefined;
  if (array.length === 0) return undefined;
  return array;
};

export type { RouterQueryT };
